import { observer } from "mobx-react-lite";

import { dataAttribute, DataAttributes, Spinner } from "@bps/fluent-ui";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { PromiseObservableButton } from "@ui-components/PromiseObservableButton/PromiseObservableButton.tsx";

import { ConditionConsultsExportDialogProps } from "./ConditionConsultsExportDialog.tsx";

type ConditionConsultsExportButtonProps = Omit<
  ConditionConsultsExportDialogProps,
  "diagnosis" | "claimBadgeText" | "patientName" | "patientId" | "providerId"
> & {
  onClick: () => void;
  clinicalRecord: ClinicalRecord;
  episodeOfCare?: EpisodeOfCare;
};

export const ConditionConsultsExportButton: React.FC<ConditionConsultsExportButtonProps> =
  observer(props => {
    const {
      onClick,
      claimId,
      claimNumber,
      clinicalRecord,
      episodeOfCareId,
      episodeOfCare
    } = props;

    const patientId = clinicalRecord.patient?.id ?? "";

    const openConsultsExportDialog = () => {
      onClick();
    };

    const {
      onExportToEditorButtonClick,
      getInitialItems,
      onExportToPdfButtonClick
    } = usePatientRecordScreenContext();

    return (
      <DataFetcher
        fetch={async () => await getInitialItems(patientId, episodeOfCareId)}
        fallback={<Spinner />}
      >
        {encounters => {
          return (
            <PromiseObservableButton
              iconProps={{ iconName: "export" }}
              borderless
              text="Export"
              onClick={openConsultsExportDialog}
              items={[
                {
                  ...dataAttribute(DataAttributes.Element, "export-to-pdf"),
                  key: "export-to-pdf",
                  text: "Export all consults to PDF",
                  onClick: () =>
                    onExportToPdfButtonClick({
                      claimId,
                      claimNumber,
                      diagnosis: episodeOfCare?.primaryDiagnosis,
                      episodeOfCareId,
                      patientId,
                      encounters
                    })
                },
                {
                  ...dataAttribute(DataAttributes.Element, "export-to-editor"),
                  key: "export-to-editor",
                  text: "Export all consults to text editor",
                  onClick: () =>
                    onExportToEditorButtonClick({
                      claimId,
                      claimNumber,
                      episodeOfCareId,
                      patientId,
                      diagnosis: episodeOfCare?.primaryDiagnosis,
                      encounters
                    })
                }
              ]}
              {...dataAttribute(DataAttributes.Element, "export-consults")}
            />
          );
        }}
      </DataFetcher>
    );
  });
